<script setup lang="ts">
import CoinsHeaderBanner from '@qicgroupdoha/loyalty-components/components/CoinsHeaderBanner';
import CoinsWheelPopup from '@qicgroupdoha/loyalty-components/components/CoinsWheelPopup';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useQuoteStore } from '@/entities/Quote';
import { GA_EVENTS, useGoogleAnalytics } from '@/shared/analytics';
import { TERMS_CONDITIONS_URL } from '../lib/B2S.contants';

const isModalOpen = ref(false);

const gaService = useGoogleAnalytics();
const route = useRoute();
const quoteStore = useQuoteStore();

const currentPath = computed(() => {
  const parts = route.path.split('/');
  return parts[parts.length - 1];
});
const page = computed(() => (currentPath.value === 'plans' ? 'select_plan' : 'your_quote'));
const showBanner = computed(() => currentPath.value === 'quote' || currentPath.value === 'plans');
const quoteParams = computed(() => {
  if (page.value === 'select_plan') {
    return {};
  }
  return {
    plan_name: quoteStore.isTplTariff ? 'TPL' : 'OD',
    quote_id: quoteStore.state.quoteId,
  };
});

function openModal() {
  isModalOpen.value = true;
  gaService.send(GA_EVENTS.VIEW_POPUP, {
    product: 'carIns',
    category: 'OCR',
    popup_name: 'b2s_to_get_more_coins',
    page: page.value,
  });
}

function handleBannerClick() {
  gaService.send(GA_EVENTS.CLICK_BANNER, {
    banner_name: 'b2s_to_get_more_coins',
    page: page.value,
    ...quoteParams.value,
  });
  openModal();
}

function handleCoinsClick() {
  gaService.send(GA_EVENTS.CLICK_ICON, {
    page: page.value,
    icon_title: 'b2s_logo',
    ...quoteParams.value,
  });
  openModal();
}

function handleLinkClick() {
  gaService.send(GA_EVENTS.CLICK_LINK, {
    link_title: 'terms_and_conditions',
    page: page.value,
    popup_name: 'b2s_to_get_more_coins',
  });
}

function handleClose(action: 'close' | 'lets_go') {
  isModalOpen.value = false;
  gaService.send(GA_EVENTS.CLICK_BTN, {
    popup_name: 'b2s_to_get_more_coins',
    btn_title: action,
    page: page.value,
  });
}

function handleBannerClose() {
  gaService.send(GA_EVENTS.CLICK_BTN, {
    btn_title: 'close',
    page: page.value,
    ...quoteParams.value,
  });
}
</script>

<template>
  <div v-if="showBanner" class="b2s-banner">
    <CoinsHeaderBanner
      :title="$t('b2s_2025.banner.text2')"
      :title-mobile="$t('b2s_2025.banner.text6')"
      @click="handleBannerClick"
      @click-coins="handleCoinsClick"
      @close="handleBannerClose"
    />
  </div>
  <CoinsWheelPopup
    :is-open="isModalOpen"
    :title="$t('b2s_2025.banner.text5')"
    :subtitle="$t('renew.b2s_2025.popup.text')"
    :button-text="$t('renew.b2s_2025.popup.button')"
    :footnote-text="$t('renew.b2s_2025.popup.learn_more_about')"
    :footnote-link-text="$t('qic.checkout.terms-link')"
    :footnote-link-url="TERMS_CONDITIONS_URL"
    class="b2s-banner__popup"
    @close="() => handleClose('close')"
    @lets-go="() => handleClose('lets_go')"
    @click-link="handleLinkClick"
  />
</template>

<style lang="scss">
.b2s-banner__popup h2.coins-wheel-popup__title {
  max-width: min(398px, 82vw);
}
</style>

<style scoped lang="scss">
@use '@styles/utils' as *;

.b2s-banner {
  &:deep([data-expanded='false']) {
    position: absolute;
    inset-block-start: 80px;
    inset-inline-end: 48px;

    @include mobile-flow {
      inset-block-start: 48px;
      inset-inline-end: 16px;
    }
  }
}
</style>
